import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import doctorService from "../../Services/doctorService";
import DoctorDetailsModal from "./DoctorDetailsModal";

const Doctors = () => {
  const [searchData, setSearchData] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const getDoctors = async () => {
      try {
        setLoading(true);
        const response = await doctorService.fetchAllDoctors();
        setPatients(response.doctors);
      } catch {
        toast.error("Error fetching doctors");
      } finally {
        setLoading(false);
      }
    };
    getDoctors();
  }, []);

  useEffect(() => {
    const result =
      patients?.filter((item) =>
        item?.name?.toLowerCase().includes(searchData.toLowerCase()),
      ) || [];
    setFilteredData(result);
  }, [searchData, patients]);

  const handleViewClick = (doctor) => {
    setSelectedDoctor(doctor);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDoctor(null);
  };

  const colWidths = ["70px", "160px", "160px", "130px", "160px", "120px"];
  const ColGroup = () => (
    <colgroup>
      {colWidths.map((w, i) => (
        <col key={i} style={{ width: w, minWidth: w }} />
      ))}
    </colgroup>
  );

  return (
    <div className="p-3 sm:p-4 font-sans">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-xs font-medium tracking-widest uppercase text-gray-500">
            Doctors
          </p>
          <p className="text-[11px] text-gray-400 mt-0.5">
            {filteredData?.length || 0} records found
          </p>
        </div>
        <Link to="AddEditDoctor">
          <button className="flex items-center gap-1.5 px-8 text-[13px] py-3 text-xs font-medium text-white bg-[#1F2937] rounded-lg hover:bg-[#111827] transition focus:outline-none focus:ring-2 focus:ring-gray-300">
            + New Doctor
          </button>
        </Link>
      </div>

      {/* ── Search ── */}
      <div className="relative mb-4">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
          />
        </svg>
        <input
          type="search"
          placeholder="Search by name..."
          value={searchData}
          onChange={(e) => setSearchData(e.target.value)}
          className="w-full sm:w-72 pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-lg bg-white
            text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-100"
        />
      </div>

      {/* ══ MOBILE CARDS ══ */}
      <div className="block sm:hidden space-y-2">
        {loading ? (
          <div className="text-center py-10 text-gray-400 text-xs">
            Loading...
          </div>
        ) : filteredData.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-xs">
            No doctors found
          </div>
        ) : (
          filteredData.map((doctor) => (
            <div
              key={doctor?.id}
              className="border border-gray-200 rounded-xl bg-white px-3 py-3"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div>
                    <p className="text-[12px] font-semibold text-gray-800">
                      {doctor?.name}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {doctor?.specialization}
                    </p>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {[
                  { label: "Contact", value: doctor?.phone_no },
                  { label: "Qualification", value: doctor?.qualification },
                ].map(({ label, value }) =>
                  value ? (
                    <div key={label} className="flex justify-between py-1.5">
                      <span className="text-[11px] text-gray-400 w-20 flex-shrink-0">
                        {label}
                      </span>
                      <span className="text-[11px] text-gray-700 text-right flex-1 ml-2 truncate">
                        {value}
                      </span>
                    </div>
                  ) : null,
                )}
              </div>
              <div className="flex gap-2 mt-2 pt-2 border-t border-gray-100">
                <button
                  onClick={() => handleViewClick(doctor)}
                  className="flex-1 py-1.5 text-[11px] font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
                >
                  View
                </button>
                <Link to="AddEditDoctor" className="flex-1">
                  <button className="w-full py-1.5 text-[11px] font-semibold rounded-lg bg-[#1F2937] text-white hover:bg-[#111827] transition">
                    Update
                  </button>
                </Link>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ══ DESKTOP TABLE ══ */}
      <div className="hidden sm:block border rounded-xl overflow-hidden">
        <div className="overflow-y-auto" style={{ maxHeight: "520px" }}>
          <table
            className="w-full text-sm"
            style={{
              tableLayout: "fixed",
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
          >
            <ColGroup />
            <thead>
              <tr>
                {[
                  "Sr No.",
                  "Name",
                  "Specialization",
                  "Contact",
                  "Qualification",
                  "Action",
                ].map((label, i) => (
                  <th
                    key={label}
                    className={`sticky top-0 z-20 bg-[#1F2937] text-left px-4 py-2.5 text-[11px]
                      text-gray-100 uppercase font-medium
                      ${i < 5 ? "border-r border-white/10" : ""}`}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-10 text-gray-400 text-[12px]"
                  >
                    Loading...
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-10 text-gray-400 text-[12px]"
                  >
                    No doctors found
                  </td>
                </tr>
              ) : (
                filteredData.map((doctor, index) => (
                  <tr
                    key={doctor?.id}
                    className={`border-b border-gray-100 hover:bg-green-50 transition-colors ${index % 2 ? "bg-gray-50" : "bg-white"}`}
                  >
                    <td className="px-4 py-2.5 text-[12px] font-semibold text-gray-500 border-r border-gray-100">
                      {doctor?.id}.
                    </td>
                    <td className="px-4 py-2.5 border-r border-gray-100">
                      <span className="text-[12px] font-medium text-gray-800 truncate">
                        {doctor?.name}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-gray-600 border-r border-gray-100 truncate">
                      {doctor?.specialization}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-gray-600 border-r border-gray-100">
                      {doctor?.phone_no}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-gray-600 border-r border-gray-100 truncate">
                      {doctor?.qualification}
                    </td>
                    <td className="px-4 py-2.5 text-[12px]">
                      <div className="flex items-center justify-start gap-3">
                        <button
                          onClick={() => handleViewClick(doctor)}
                          className="text-[11px] font-medium text-white bg-[#1F2937] px-2.5 py-1 rounded-lg hover:bg-[#111827] transition"
                        >
                          View
                        </button>
                        <Link to="AddEditDoctor">
                          <button className="text-[11px] font-medium text-white bg-[#1F2937] px-2.5 py-1 rounded-lg hover:bg-[#111827] transition">
                            Update
                          </button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <td
                  colSpan={6}
                  className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-[12px]
                    font-medium text-gray-100 border-t border-white/10"
                >
                  {filteredData?.length || 0} total records
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <DoctorDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        doctorDetails={selectedDoctor}
      />
    </div>
  );
};

export default Doctors;
